import type { User } from '@prisma/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  SafeUser,
  TokenResponse,
  UpdateOnboardingParams,
  UpdateProfileParams,
  UpdateUserParams,
  type RegisterParams,
} from '@zond/api-interfaces';
import type { AxiosError } from 'axios';
import { client } from '../../../client';

export const useMe = () =>
  useQuery<User, AxiosError>({
    queryKey: ['user'],
    queryFn: () => client.get(`/user`).then((res) => res.data),
    retry: false,
  });

export const useSignUp = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<TokenResponse, AxiosError, RegisterParams>({
    mutationFn: (data) => client.post(`/auth/register`, data).then((res) => res.data),
    onSuccess,
    onError,
  });

export const useUsers = () =>
  useQuery<SafeUser[], AxiosError>({
    queryKey: ['users'],
    queryFn: () => client.get(`/users`).then((res) => res.data),
  });

export const useUpdateUser = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<User, AxiosError, Partial<User>>({
    mutationFn: (data) => client.put(`/users/${data.id}`, data).then((res) => res.data),
    onSuccess,
    onError,
  });

export const useDeleteUser = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<User, AxiosError, string>({
    mutationFn: (id) => client.delete(`/users/${id}`).then((res) => res.data),
    onSuccess,
    onError,
  });

export const useUpdateProfile = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<SafeUser, AxiosError, UpdateProfileParams>({
    mutationFn: (data) => client.post(`/user/update-profile`, data).then((res) => res.data),
    onSuccess,
    onError,
  });

export const useUpdateOnboarding = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<UpdateOnboardingParams, AxiosError, UpdateOnboardingParams>({
    mutationFn: (data) => client.post(`/user/update-onboarding`, data).then((res) => res.data),
    onSuccess,
    onError,
  });

export const useUpdateUserProfile = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<SafeUser, AxiosError, Partial<UpdateUserParams>>({
    mutationFn: (data) => client.patch(`/user`, data).then((res) => res.data),
    onSuccess,
    onError,
  });

export const useUpdateAvatar = (onSuccess?: () => void, onError?: () => void) =>
  useMutation<SafeUser, AxiosError, File>({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return client.put(`/user/avatar`, formData).then((res) => res.data);
    },
    onSuccess,
    onError,
  });
